import React from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaRocket, FaChartBar, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ProcessSection = () => {

  const navigate = useNavigate()

  const processSteps = [
    {
      number: 1,
      icon: <FaSearch />,
      title: 'Discovery',
      description: 'We analyze your business, goals, and target audience to create a tailored strategy.',
      color: 'primary'
    },
    {
      number: 2,
      icon: <FaLightbulb />,
      title: 'Strategy',
      description: 'We develop a comprehensive plan with clear objectives and measurable outcomes.',
      color: 'warning'
    },
    {
      number: 3,
      icon: <FaRocket />,
      title: 'Execution',
      description: 'We implement the strategy with precision and attention to every detail.',
      color: 'success'
    },
    {
      number: 4,
      icon: <FaChartBar />,
      title: 'Optimization',
      description: 'We continuously monitor, analyze, and optimize for maximum results.',
      color: 'info'
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const item = {
    hidden: {
      y: 50,
      opacity: 0,
      scale: 0.9
    },
    show: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const cardHover = {
    y: -4,
    scale: 1.01,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  };

  const iconHover = {
    scale: 1.1,
    rotate: 5,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  };

  return (
    <section className="py-5 position-relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{
        background: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)'
      }}></div>

      <div className="container position-relative">
        <motion.div
          className="text-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <motion.span
            className="badge bg-primary-subtle text-primary mb-3 px-3 py-2 rounded-pill"
            style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', fontWeight: '500' }}
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our Process
          </motion.span>
          <h2 className="fw-bold mb-4 gradient-text-animated" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            How We Deliver Results
          </h2>
          <p className="lead text-muted mx-auto" style={{
            maxWidth: 'clamp(20rem, 80vw, 48rem)',
            fontSize: 'clamp(1rem, 2vw, 1.1rem)',
            lineHeight: '1.6'
          }}>
            Our proven 4-step process ensures we deliver exceptional results for every client, every time.
          </p>
        </motion.div>

        <motion.div
          className="row g-3"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              className="col-12 col-sm-6 col-lg-3"
              variants={item}
              whileHover={cardHover}
            >
              <motion.div
                className="card h-100 border-0 position-relative overflow-hidden card-hover-effect d-flex flex-column text-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                  borderRadius: '20px'
                }}
              >
                {/* Gradient Overlay */}
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                  background: `linear-gradient(135deg, ${step.color === 'primary' ? 'rgba(59, 130, 246, 0.02)' :
                    step.color === 'warning' ? 'rgba(245, 158, 11, 0.02)' :
                      step.color === 'success' ? 'rgba(16, 185, 129, 0.02)' :
                        'rgba(6, 182, 212, 0.02)'} 0%, transparent 50%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}></div>

                <div className="card-body p-3 p-md-4 d-flex flex-column position-relative flex-grow-1">
                  <div className="flex-grow-1 d-flex flex-column justify-content-center">
                    {/* Process Number */}
                    <motion.div
                      className="position-absolute top-0 start-0 m-3"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                      viewport={{ once: true }}
                    >
                      <div className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold" style={{
                        width: '40px',
                        height: '40px',
                        background: 'linear-gradient(135deg, #6b7280, #374151)',
                        fontSize: '1.1rem',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                      }}>
                        {step.number}
                      </div>
                    </motion.div>
                    {/* Icon */}
                    <motion.div
                      className="mb-4"
                      whileHover={iconHover}
                    >
                      <motion.div
                        className="d-inline-flex align-items-center justify-content-center rounded-4 text-white position-relative service-icon-wrapper service-icon-float"
                        style={{
                          width: 'clamp(50px, 8vw, 70px)',
                          height: 'clamp(50px, 8vw, 70px)',
                          background: step.color === 'primary' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' :
                            step.color === 'warning' ? 'linear-gradient(135deg, #f59e0b, #d97706)' :
                              step.color === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' :
                                'linear-gradient(135deg, #06b6d4, #0891b2)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                        }}
                      >
                        <span style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}>{step.icon}</span>
                      </motion.div>
                    </motion.div>
                    <h5 className="fw-bold mb-2" style={{
                      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                      color: '#1e293b'
                    }}>
                      {step.title}
                    </h5>
                    <p className="text-muted mb-0" style={{
                      lineHeight: '1.6',
                      fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)'
                    }}>
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow for connection */}
                  {index < processSteps.length - 1 && (
                    <motion.div
                      className="position-absolute top-50 end-0 translate-middle-y d-none d-lg-block"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 + (index * 0.1) }}
                      viewport={{ once: true }}
                      style={{ transform: 'translateY(-50%)' }}
                    >
                      <div className="d-inline-flex align-items-center justify-content-center rounded-circle text-primary" style={{
                        width: '30px',
                        height: '30px',
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '2px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        <FaArrowRight style={{ fontSize: '0.8rem' }} />
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="text-center mt-4 mt-md-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          onClick={()=>navigate('/contact')}
        >
          <motion.button
            className="btn btn-primary btn-lg px-4 px-md-5 py-2 py-md-3 rounded-pill d-inline-flex align-items-center justify-content-center gap-2 btn-pulse"
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              fontWeight: '600',
              fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
              minWidth: 'clamp(200px, 25vw, 250px)'
            }}
            whileHover={{
              scale: 1.05,
              boxShadow: '0 12px 35px rgba(59, 130, 246, 0.4)',
              transition: { duration: 0.3 }
            }}
          >
            Start Your Project
            <FaArrowRight style={{ fontSize: 'clamp(0.8rem, 1.5vw, 1rem)' }} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
